import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/Table/Table';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';
import { useState } from 'react';

import { getRenderer } from '../renderers/registry';
import { ColumnAnalysis, CellValue } from '../types';

import styles from './PreviewGrid.module.scss';

interface PreviewGridProps {
  headers: string[];
  rows: Record<string, CellValue>[];
  // 1-based original row number for each row in `rows`
  rowNumbers: number[];
  analyses: ColumnAnalysis[];
  onOpenCell: (rowIndex: number, columnId: string) => void;
}

const isHeavy = (kind: string) => kind !== 'plain';

export const PreviewGrid = ({ headers, rows, rowNumbers, analyses, onOpenCell }: PreviewGridProps) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  const kindByColumn = new Map(analyses.map(a => [a.id, a.kind]));

  return (
    <div className={styles.wrapper}>
      <Table tableClassName={styles.scroll}>
        <TableHeader>
          <TableRow>
            <TableHead className={clsx(styles.numHead, styles.sticky)}>#</TableHead>
            {headers.map(header => (
              <TableHead
                key={header}
                className={styles.colHead}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const isOpen = expanded === rowIndex;
            return (
              <TableRow
                key={rowNumbers[rowIndex]}
                className={isOpen ? styles.openRow : undefined}
              >
                <TableCell
                  className={clsx(styles.numCell, styles.sticky)}
                  onClick={() => setExpanded(isOpen ? null : rowIndex)}
                >
                  <span className={styles.numInner}>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {rowNumbers[rowIndex]}
                  </span>
                </TableCell>
                {headers.map(header => {
                  const kind = kindByColumn.get(header) ?? 'plain';
                  const renderer = getRenderer(kind);
                  const Cell = isOpen ? renderer.Expanded : renderer.Compact;
                  const heavy = isHeavy(kind);
                  return (
                    <TableCell
                      key={header}
                      className={isOpen ? styles.cellExpanded : styles.cell}
                    >
                      <div className={styles.cellInner}>
                        <div className={clsx(styles.cellContent, isOpen && styles.cellContentExpanded)}>
                          <Cell value={row[header]} />
                        </div>
                        {heavy && (
                          <button
                            type="button"
                            className={styles.openBtn}
                            aria-label="Open full width"
                            onClick={() => onOpenCell(rowIndex, header)}
                          >
                            <Maximize2 size={12} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
